import { useCallback, useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'

import Button from '../button/Button'
import StepUpDialog from '../step-up-dialog/StepUpDialog'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
} from '../../_data/verifications'
import { BannerTone } from './types'

interface PlaidConnectButtonProps {
  quickPassUserId: string
  isConnected: boolean
  onStatusMessage: (message: string, tone: BannerTone) => void
}

/**
 * Opens Plaid Link to connect a bank, then exchanges the public token so the
 * backend can verify funds + income. The link token is fetched lazily on
 * click (Plaid Link can't open until it has one), then the modal auto-opens
 * once Plaid reports ready.
 *
 * A step-up check runs before any of that. Connecting a bank is the highest
 * consequence action in QuickPass and a password on its own is a single factor,
 * so the renter proves themselves again with a one-time code before Plaid Link
 * is ever surfaced. The server enforces the same rule on `createPlaidLinkToken`,
 * so skipping this dialog gets a caller nowhere.
 *
 * @param {PlaidConnectButtonProps} props
 * @returns {JSX.Element}
 */
const PlaidConnectButton = ({
  quickPassUserId,
  isConnected,
  onStatusMessage,
}: PlaidConnectButtonProps): JSX.Element => {
  const { refreshProfile } = useQuickPassProfile()
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [shouldOpen, setShouldOpen] = useState<boolean>(false)
  const [isStepUpOpen, setIsStepUpOpen] = useState<boolean>(false)

  const handleSuccess = useCallback(
    async (publicToken: string): Promise<void> => {
      setIsBusy(true)
      const result = await exchangePlaidPublicToken(
        quickPassUserId,
        publicToken,
      )
      await refreshProfile()
      setIsBusy(false)
      setLinkToken(null)

      if (result === null) {
        onStatusMessage(
          'Could not finish connecting your bank. Please try again.',
          'error',
        )
        return
      }

      onStatusMessage(
        'Bank connected! Your verified income and funds are updating.',
        'info',
      )
    },
    [quickPassUserId, refreshProfile, onStatusMessage],
  )

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: publicToken => {
      void handleSuccess(publicToken)
    },
    onExit: () => {
      setShouldOpen(false)
      setIsBusy(false)
    },
  })

  useEffect(() => {
    if (shouldOpen === true && ready === true && linkToken !== null) {
      setShouldOpen(false)
      open()
    }
  }, [shouldOpen, ready, linkToken, open])

  const handleStepUpSatisfied = useCallback(async (): Promise<void> => {
    setIsStepUpOpen(false)
    setIsBusy(true)
    onStatusMessage('', 'info')

    const token = await createPlaidLinkToken(quickPassUserId)
    if (token === null) {
      setIsBusy(false)
      onStatusMessage(
        'Bank connections are not available right now. Try again later.',
        'error',
      )
      return
    }

    setLinkToken(token)
    setShouldOpen(true)
    setIsBusy(false)
  }, [quickPassUserId, onStatusMessage])

  return (
    <>
      <Button
        type="button"
        variant={isConnected ? 'secondary' : 'primary'}
        size="small"
        isLoading={isBusy}
        onClick={() => {
          onStatusMessage('', 'info')
          setIsStepUpOpen(true)
        }}
      >
        {isConnected ? 'Reconnect bank' : 'Connect bank'}
      </Button>
      <StepUpDialog
        isOpen={isStepUpOpen}
        quickPassUserId={quickPassUserId}
        onCancel={() => {
          setIsStepUpOpen(false)
        }}
        onSatisfied={() => {
          void handleStepUpSatisfied()
        }}
      />
    </>
  )
}

export default PlaidConnectButton
